import React from 'react'
import { createPortal } from 'react-dom'
import { api } from '../api/client'
import { useApp } from '../context/AppContext'
import { useDialog } from '../hooks/useDialog'
import ClientNotice, { type ClientNoticeData } from '../privacy/ClientNotice'
import { saveErrorMessage } from '../screens/settings/toast'
import { Loading } from '../ui/States'
import '../components/Sheet.css'
// Its z-index (18) puts .settings-sheet above .settings-panel (16), so the sheet covers Settings when opened from Settings → Privacy.
import '../screens/settings/Settings.css'
import './ClientNoticeSheet.css'

type LoadState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; notice: ClientNoticeData | null }

// ClientNoticeSheet shows her the privacy notice her clients read, built from
// her business details, so she can say it looks right or go and change what it
// says. Until her business details say where she works there is nothing worth
// showing yet: only the way to Business details.
export default function ClientNoticeSheet({ onClose }: { onClose: () => void }) {
  const { dispatch } = useApp()
  const [loaded, setLoaded] = React.useState<LoadState>({ kind: 'loading' })
  const [attempt, setAttempt] = React.useState(0)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })

  React.useEffect(() => {
    let live = true
    setLoaded({ kind: 'loading' })
    api.clientNotice().then(
      (notice) => { if (live) setLoaded({ kind: 'ready', notice }) },
      () => { if (live) setLoaded({ kind: 'error' }) },
    )
    return () => { live = false }
  }, [attempt])

  // Looks right: the check is saved, then her session is read again so the
  // step on her Today list ticks itself off.
  const looksRight = async () => {
    setBusy(true)
    setError('')
    try {
      await api.clientNoticeChecked()
      dispatch({ type: 'SESSION_STARTED', payload: await api.me() })
      onClose()
    } catch (e) {
      setError(saveErrorMessage(e))
      setBusy(false)
    }
  }

  const changeDetails = () => {
    dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'business' })
    onClose()
  }

  // Without a business there is nothing to fill in. Without a location the
  // notice still names her and how to reach her, and her booking page waits
  // for this check, so it is hers to confirm.
  const notice = loaded.kind === 'ready' ? loaded.notice : null
  const title = 'Your privacy notice for clients'
  const sheet = (
    <div className="sheet-overlay settings-sheet" onClick={() => { if (!busy) onClose() }}>
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <div className="sheet-header"><h2>{title}</h2></div>
        {loaded.kind === 'loading' && <Loading inline />}
        {loaded.kind === 'error' && (
          <div className="settings-status">
            <p role="alert">Couldn’t load your notice.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          </div>
        )}
        {loaded.kind === 'ready' && notice === null && (
          <>
            <p className="client-notice-line">Add your business details first</p>
            <div className="sheet-actions sheet-actions--single">
              <button type="button" className="btn btn-primary" onClick={changeDetails}>Change my details</button>
            </div>
          </>
        )}
        {loaded.kind === 'ready' && notice !== null && (
          <>
            {/* Harmovi speaking to her, above the notice; the notice itself,
                in her voice to her clients, sits in its own panel below. */}
            <p className="client-notice-line">This is what your clients read when they book. Harmovi writes it from your name, business name, address or area and phone number.</p>
            <div className="client-notice">
              <ClientNotice notice={notice} level={3} />
            </div>
            <div className="client-notice-actions">
              {error && <p className="field-error" role="alert">{error}</p>}
              <div className="sheet-actions sheet-actions--single">
                <button type="button" className="btn btn-primary" onClick={looksRight} disabled={busy}>{busy ? 'Saving…' : 'Looks right'}</button>
                <button type="button" className="btn btn-secondary" onClick={changeDetails} disabled={busy}>Change my details</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )

  // The sheet renders into the app shell rather than into the scrolling
  // screen, as ExpenseSheet does, for the same reason: the tab bar's
  // backdrop-filter makes it its own stacking context, so it paints over
  // anything nested in the screen and no z-index on the overlay can win.
  // Mounted there the sheet sits above the tab bar and the + button.
  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
